import React from 'react';
import {
  Heading,
  Type,
  Image as ImageIcon,
  MousePointerClick,
  Minus,
  Maximize2,
  Columns,
  LayoutTemplate,
  Layers,
  Sparkles,
  Megaphone,
  Store,
  Grid,
  ShoppingBag,
  HelpCircle,
  MessageSquareQuote,
  FileText,
  Smartphone,
  ExternalLink,
  ChevronDown,
  Star,
  Globe,
  MapPin,
  Compass,
  Video,
  PhoneCall,
  ListOrdered,
  Award,
  ShieldCheck,
} from 'lucide-react';

export const WIDGET_CATEGORIES = [
  { id: 'basic', label: 'Basic Widgets' },
  { id: 'layout', label: 'Layout & Spacing' },
  { id: 'cards', label: 'Cards & Content' },
  { id: 'banners', label: 'Banners & Sliders' },
  { id: 'collections', label: 'Collections & Dynamic' },
  { id: 'global', label: 'Global Website Parts' },
];

export const WIDGET_DEFINITIONS = {
  // ----------------------------------------------------
  // BASIC WIDGETS
  // ----------------------------------------------------
  heading: {
    type: 'heading',
    name: 'Heading',
    category: 'basic',
    icon: Heading,
    description: 'Add a prominent headline with SEO tags.',
    defaultContent: {
      text: 'Add Your Heading Text Here',
      level: 'h2', // h1, h2, h3, h4, h5, h6
    },
    defaultStyle: {
      color: '#1e293b',
      fontSize: '28px',
      fontWeight: '700',
      alignment: 'left', // left, center, right
      marginBottom: '16px',
    },
    render: ({ content = {}, style = {} }) => {
      const Tag = content.level || 'h2';
      const styles = {
        color: style.color || '#1e293b',
        fontSize: style.fontSize || '28px',
        fontWeight: style.fontWeight || '700',
        textAlign: style.alignment || 'left',
        marginBottom: style.marginBottom || '16px',
        lineHeight: '1.2',
      };
      return <Tag style={styles} className="tracking-tight">{content.text || 'Heading text'}</Tag>;
    },
  },

  text: {
    type: 'text',
    name: 'Text Editor',
    category: 'basic',
    icon: Type,
    description: 'Rich paragraph text with markdown and formatting.',
    defaultContent: {
      content: 'Craft your engaging message here. Blank lines create separate paragraphs, while dashes create bulleted lists for clean presentation.',
    },
    defaultStyle: {
      color: '#475569',
      fontSize: '16px',
      lineHeight: '1.65',
      alignment: 'left',
      marginBottom: '16px',
    },
    render: ({ content = {}, style = {} }) => {
      const text = content.content || '';
      const paragraphs = text.split(/\n{2,}/).filter(Boolean);
      return (
        <div
          style={{
            color: style.color || '#475569',
            fontSize: style.fontSize || '16px',
            lineHeight: style.lineHeight || '1.65',
            textAlign: style.alignment || 'left',
            marginBottom: style.marginBottom || '16px',
          }}
          className="space-y-3"
        >
          {paragraphs.length > 0 ? (
            paragraphs.map((para, idx) => {
              if (/^[-*]\s+/.test(para)) {
                const items = para.split('\n').filter(Boolean);
                return (
                  <ul key={idx} className="list-disc pl-5 space-y-1">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace(/^[-*]\s+/, '')}</li>
                    ))}
                  </ul>
                );
              }
              return <p key={idx} className="whitespace-pre-line">{para}</p>;
            })
          ) : (
            <p className="text-gray-400 italic">Empty text block</p>
          )}
        </div>
      );
    },
  },

  image: {
    type: 'image',
    name: 'Image',
    category: 'basic',
    icon: ImageIcon,
    description: 'Showcase photos, graphics, or device shots.',
    defaultContent: {
      url: '/assets/buy-phone.png',
      alt: 'ZOBOX Image',
      caption: '',
      link: '',
    },
    defaultStyle: {
      width: '100%',
      maxWidth: '100%',
      borderRadius: '8px',
      objectFit: 'contain',
      alignment: 'center',
    },
    render: ({ content = {}, style = {} }) => {
      const alignClass = style.alignment === 'center' ? 'mx-auto' : style.alignment === 'right' ? 'ml-auto' : '';
      return (
        <figure className={`my-2 ${alignClass}`} style={{ maxWidth: style.maxWidth || '100%' }}>
          {content.url ? (
            <img
              src={content.url}
              alt={content.alt || ''}
              style={{
                borderRadius: style.borderRadius || '8px',
                objectFit: style.objectFit || 'contain',
                maxHeight: '400px',
              }}
              className="w-full h-auto"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect fill="%23f1f5f9" width="300" height="200"/><text fill="%2394a3b8" font-family="sans-serif" font-size="14" dy="10.5" font-weight="bold" x="50%" y="50%" text-anchor="middle">Image Not Found</text></svg>';
              }}
            />
          ) : (
            <div className="w-full h-40 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs border border-dashed">
              Select or upload an image in settings
            </div>
          )}
          {content.caption && (
            <figcaption className="text-xs text-slate-500 text-center mt-1.5 italic">
              {content.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },

  button: {
    type: 'button',
    name: 'Button',
    category: 'basic',
    icon: MousePointerClick,
    description: 'Call-to-action button linking to internal routes or external URLs.',
    defaultContent: {
      label: 'Explore Devices',
      url: '/buy',
      external: false,
      new_tab: false,
    },
    defaultStyle: {
      variant: 'primary', // primary, secondary, outline, danger
      alignment: 'left',
      size: 'medium', // small, medium, large
      borderRadius: '8px',
    },
    render: ({ content = {}, style = {} }) => {
      const isSec = style.variant === 'secondary';
      const isOutline = style.variant === 'outline';
      const isDng = style.variant === 'danger';

      let btnClass = 'bg-[#2b3380] text-white hover:bg-[#1e2669] shadow-sm';
      if (isSec) btnClass = 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300';
      if (isOutline) btnClass = 'border-2 border-[#2b3380] text-[#2b3380] hover:bg-indigo-50/50';
      if (isDng) btnClass = 'bg-red-600 text-white hover:bg-red-700';

      const sizeClass = style.size === 'small' ? 'px-3 py-1.5 text-xs' : style.size === 'large' ? 'px-6 py-3 text-base' : 'px-4 py-2 text-sm';
      const alignClass = style.alignment === 'center' ? 'text-center' : style.alignment === 'right' ? 'text-right' : 'text-left';

      return (
        <div className={`my-2 ${alignClass}`}>
          <span
            style={{ borderRadius: style.borderRadius || '8px' }}
            className={`inline-flex items-center gap-2 font-semibold transition-all cursor-pointer select-none ${btnClass} ${sizeClass}`}
          >
            <span>{content.label || 'Click Here'}</span>
            {content.external ? <ExternalLink className="w-3.5 h-3.5" /> : <span>→</span>}
          </span>
        </div>
      );
    },
  },

  divider: {
    type: 'divider',
    name: 'Divider',
    category: 'layout',
    icon: Minus,
    description: 'Horizontal separator line between sections.',
    defaultContent: {},
    defaultStyle: {
      color: '#e2e8f0',
      thickness: '1px',
      styleType: 'solid', // solid, dashed, dotted
      marginVertical: '24px',
    },
    render: ({ style = {} }) => (
      <hr
        style={{
          borderColor: style.color || '#e2e8f0',
          borderTopWidth: style.thickness || '1px',
          borderStyle: style.styleType || 'solid',
          margin: `${style.marginVertical || '24px'} 0`,
        }}
        className="w-full border-0 border-t"
      />
    ),
  },

  spacer: {
    type: 'spacer',
    name: 'Spacer',
    category: 'layout',
    icon: Maximize2,
    description: 'Add adjustable blank vertical whitespace.',
    defaultContent: {},
    defaultStyle: {
      height: '32px',
    },
    render: ({ style = {} }) => (
      <div
        style={{ height: style.height || '32px' }}
        className="w-full relative group flex items-center justify-center"
      >
        <span className="text-[10px] text-slate-400 opacity-30 select-none">
          ↕ {style.height || '32px'}
        </span>
      </div>
    ),
  },

  // ----------------------------------------------------
  // CMS PAGE & CARDS WIDGETS
  // ----------------------------------------------------
  page_hero: {
    type: 'page_hero',
    name: 'Page Hero Banner',
    category: 'cards',
    icon: LayoutTemplate,
    description: 'Dedicated page header with eyebrow, title, excerpt, and CTA.',
    defaultContent: {
      eyebrow: 'SHOP ZOBOX',
      title: 'Choose the technology you need.',
      excerpt: 'Explore our certified refurbished mobile and laptop collections with full warranty coverage.',
      featured_image: '',
      cta_text: 'Browse Collection',
      cta_url: '/buy',
      layout: 'wave', // content, gateway, wave
    },
    defaultStyle: {
      textColor: '#1e293b',
      eyebrowColor: '#2b3380',
      alignment: 'left',
    },
    render: ({ content = {}, style = {} }) => {
      const isGateway = content.layout === 'gateway';
      return (
        <div
          className={`p-6 sm:p-8 rounded-2xl ${
            isGateway
              ? 'bg-gradient-to-r from-[#1e2669] to-[#2B3380] text-white'
              : 'bg-slate-50 border border-slate-200 text-slate-900'
          }`}
        >
          {content.eyebrow && (
            <span
              style={{ color: isGateway ? '#93c5fd' : style.eyebrowColor || '#2b3380' }}
              className="text-xs font-bold uppercase tracking-wider block mb-1"
            >
              {content.eyebrow}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            {content.title || 'Page Title'}
          </h1>
          {content.excerpt && (
            <p
              className={`text-sm sm:text-base mt-2 max-w-2xl leading-relaxed ${
                isGateway ? 'text-blue-100' : 'text-slate-600'
              }`}
            >
              {content.excerpt}
            </p>
          )}
          {content.cta_text && (
            <div className="mt-4">
              <span
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold shadow-sm ${
                  isGateway ? 'bg-white text-[#2b3380]' : 'bg-[#2b3380] text-white'
                }`}
              >
                {content.cta_text} →
              </span>
            </div>
          )}
        </div>
      );
    },
  },

  card: {
    type: 'card',
    name: 'Feature Card',
    category: 'cards',
    icon: Layers,
    description: 'Product, category, or feature card with image, title, and link.',
    defaultContent: {
      eyebrow: 'DEVICES',
      title: 'Buy Refurbished Phones',
      description: 'Find certified iPhones, Samsung, and OnePlus models tested through our 32-step inspection.',
      image: '/assets/buy-phone.png',
      alt: 'Phones',
      href: '/buyMobile',
      cta_label: 'Shop Mobiles',
      status_text: '',
    },
    defaultStyle: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      borderColor: '#e2e8f0',
    },
    render: ({ content = {}, style = {} }) => (
      <div
        style={{
          backgroundColor: style.backgroundColor || '#ffffff',
          borderRadius: style.borderRadius || '12px',
          borderColor: style.borderColor || '#e2e8f0',
        }}
        className="border p-4 shadow-sm flex flex-col justify-between h-full hover:shadow-md transition-shadow"
      >
        <div>
          {content.image && (
            <div className="w-full h-36 bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center mb-3">
              <img
                src={content.image}
                alt={content.alt || ''}
                className="max-h-full max-w-full object-contain p-2"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
          {content.eyebrow && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#2b3380] block mb-1">
              {content.eyebrow}
            </span>
          )}
          <h3 className="font-bold text-slate-900 text-base">{content.title || 'Card Title'}</h3>
          {content.description && (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{content.description}</p>
          )}
          {content.status_text && (
            <span className="inline-block mt-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">
              {content.status_text}
            </span>
          )}
        </div>
        {content.cta_label && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <span className="text-xs font-bold text-[#2b3380] flex items-center gap-1 hover:underline">
              {content.cta_label} →
            </span>
          </div>
        )}
      </div>
    ),
  },

  action_button: {
    type: 'action_button',
    name: 'Quick Action Banner',
    category: 'cards',
    icon: MousePointerClick,
    description: 'Prominent action banner or contact CTA box.',
    defaultContent: {
      title: 'Discuss an Organisation Requirement',
      href: '/contactUs',
      variant: 'primary',
    },
    defaultStyle: {
      backgroundColor: '#2b3380',
      textColor: '#ffffff',
    },
    render: ({ content = {}, style = {} }) => (
      <div
        style={{ backgroundColor: style.backgroundColor || '#2b3380', color: style.textColor || '#ffffff' }}
        className="p-5 rounded-xl flex flex-wrap items-center justify-between gap-4 shadow-sm"
      >
        <span className="font-bold text-sm sm:text-base">{content.title || 'Take Action Now'}</span>
        <span className="px-4 py-2 bg-white text-[#2b3380] font-bold rounded-lg text-xs hover:bg-slate-50 transition">
          Open Now →
        </span>
      </div>
    ),
  },

  note_box: {
    type: 'note_box',
    name: 'Notice / Note Box',
    category: 'cards',
    icon: FileText,
    description: 'Highlighted callout note for terms, policies, or tips.',
    defaultContent: {
      title: 'Commercial Terms',
      description: 'Commercial terms, device eligibility, and delivery windows are confirmed during the order review process.',
    },
    defaultStyle: {
      backgroundColor: '#fefce8',
      borderColor: '#fef08a',
      textColor: '#713f12',
    },
    render: ({ content = {}, style = {} }) => (
      <div
        style={{
          backgroundColor: style.backgroundColor || '#fefce8',
          borderColor: style.borderColor || '#fef08a',
          color: style.textColor || '#713f12',
        }}
        className="p-4 rounded-xl border text-xs space-y-1"
      >
        <strong className="block font-bold text-sm">{content.title || 'Important Notice'}</strong>
        <p className="leading-relaxed">{content.description}</p>
      </div>
    ),
  },

  // ----------------------------------------------------
  // BANNERS & SLIDERS
  // ----------------------------------------------------
  hero_banner: {
    type: 'hero_banner',
    name: 'Hero Slider Banner',
    category: 'banners',
    icon: Sparkles,
    description: 'Full-width hero banner with desktop & mobile images and CTA.',
    defaultContent: {
      heading: 'Driving Retail Success Through Technology',
      subheading: 'India\'s fastest-growing ecosystem for certified refurbished devices.',
      image: '/banners/banner1.png',
      mobile_image: '/banners/banner1.png',
      button_text: 'Partner with ZOBOX',
      button_url: '/becomePartner',
      overlay: true,
      alignment: 'left',
    },
    defaultStyle: {
      height: '320px',
    },
    render: ({ content = {}, style = {} }) => (
      <div
        style={{ height: style.height || '320px' }}
        className="w-full relative rounded-2xl overflow-hidden bg-slate-900 text-white flex items-center p-6 sm:p-12 shadow-lg"
      >
        {content.image && (
          <img
            src={content.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-60"
            onError={(e) => (e.target.style.display = 'none')}
          />
        )}
        <div className="relative z-10 max-w-xl space-y-3">
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            {content.heading || 'Hero Headline'}
          </h2>
          {content.subheading && (
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
              {content.subheading}
            </p>
          )}
          {content.button_text && (
            <div className="pt-2">
              <span className="px-5 py-2.5 bg-white text-[#2b3380] font-bold rounded-xl text-xs sm:text-sm inline-block shadow-md">
                {content.button_text} →
              </span>
            </div>
          )}
        </div>
      </div>
    ),
  },

  promo_banner: {
    type: 'promo_banner',
    name: 'Promo Callout Banner',
    category: 'banners',
    icon: Megaphone,
    description: 'Promotional strip with discount badge and group placement.',
    defaultContent: {
      heading: 'Best Deals on Refurbished Smartphones',
      badge: 'Limited Time',
      image: '/banners/banner6.png',
      button_text: 'Explore Phones',
      button_url: '/buyMobile',
      group_key: 'phones',
    },
    defaultStyle: {
      backgroundColor: '#f8fafc',
    },
    render: ({ content = {} }) => (
      <div className="p-6 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-blue-200/60 flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          {content.badge && (
            <span className="px-2.5 py-0.5 rounded-full bg-[#2b3380] text-white text-[10px] font-bold uppercase tracking-wider inline-block">
              {content.badge}
            </span>
          )}
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">{content.heading}</h3>
        </div>
        {content.button_text && (
          <span className="px-4 py-2 bg-[#2b3380] text-white text-xs font-bold rounded-lg shadow-sm">
            {content.button_text} →
          </span>
        )}
      </div>
    ),
  },

  // ----------------------------------------------------
  // COLLECTIONS & DYNAMIC WIDGETS
  // ----------------------------------------------------
  product_rail: {
    type: 'product_rail',
    name: 'Product Rail / Grid',
    category: 'collections',
    icon: ShoppingBag,
    description: 'Dynamic product grid showing live MRP, selling rate, stock, and photos.',
    defaultContent: {
      title: 'Buy Refurbished Phones',
      eyebrow: 'SHOP ZOBOX',
      category: 'Mobile',
      limit: 4,
      viewAllUrl: '/buyMobile',
    },
    defaultStyle: {
      columns: 4, // 2, 3, 4
    },
    render: ({ content = {} }) => (
      <div className="space-y-4">
        <div className="flex items-end justify-between border-b pb-3">
          <div>
            {content.eyebrow && (
              <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block mb-1">
                {content.eyebrow}
              </span>
            )}
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">{content.title}</h2>
          </div>
          {content.viewAllUrl && (
            <span className="text-xs font-bold text-[#2b3380] hover:underline cursor-pointer">
              View All ({content.category || 'All'}) →
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { name: 'Apple iPhone 13 (128GB)', rate: '32,999', mrp: '69,900', img: '/assets/buy-phone.png', grade: 'Superb' },
            { name: 'Apple iPhone 14 (128GB)', rate: '41,999', mrp: '79,900', img: '/assets/buy-phone.png', grade: 'Excellent' },
            { name: 'Samsung Galaxy S22 5G', rate: '28,499', mrp: '54,999', img: '/assets/buy-phone.png', grade: 'Good' },
            { name: 'OnePlus 10 Pro 5G', rate: '26,999', mrp: '49,999', img: '/assets/buy-phone.png', grade: 'Superb' },
          ].slice(0, content.limit || 4).map((p, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-full h-28 bg-slate-50 rounded-lg flex items-center justify-center p-2 mb-2">
                  <img src={p.img} alt="" className="max-h-full max-w-full object-contain" onError={(e) => (e.target.style.display = 'none')} />
                </div>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                  Grade: {p.grade}
                </span>
                <h4 className="font-semibold text-xs text-slate-900 mt-1 line-clamp-1">{p.name}</h4>
              </div>
              <div className="mt-3 pt-2 border-t flex items-baseline justify-between">
                <div>
                  <span className="font-bold text-green-700 text-sm">₹{p.rate}</span>
                  <span className="text-[10px] text-slate-400 line-through ml-1.5">₹{p.mrp}</span>
                </div>
                <span className="text-[10px] font-bold text-[#2b3380] bg-indigo-50 px-2 py-1 rounded">Buy</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  category_grid: {
    type: 'category_grid',
    name: 'Category Grid',
    category: 'collections',
    icon: Grid,
    description: 'Display interactive device categories (Mobiles, Laptops, etc.).',
    defaultContent: {
      title: 'Find Your Next Device',
      eyebrow: 'BUY DEVICES',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="space-y-4">
        {content.eyebrow && (
          <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block">
            {content.eyebrow}
          </span>
        )}
        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">{content.title}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { name: 'Buy Mobile', count: 'Refurbished Phones', img: '/assets/buy-phone.png', href: '/buyMobile' },
            { name: 'Buy Laptop', count: 'Refurbished Laptops', img: '/assets/buy_laptop.png', href: '/buyLaptop' },
            { name: 'Tablets', count: 'iPads & Tablets', img: '/assets/buy-phone.png', href: '/buy' },
            { name: 'Accessories', count: 'Chargers & Audio', img: '/assets/buy-phone.png', href: '/buy' },
          ].map((cat, idx) => (
            <div key={idx} className="border rounded-xl p-4 bg-white hover:border-[#2b3380] transition cursor-pointer flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center p-2 mb-2">
                <img src={cat.img} alt="" className="w-10 h-10 object-contain" onError={(e) => (e.target.style.display = 'none')} />
              </div>
              <strong className="text-sm text-slate-900">{cat.name}</strong>
              <span className="text-xs text-slate-500 mt-0.5">{cat.count}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  brand_strip: {
    type: 'brand_strip',
    name: 'Brand Strip',
    category: 'collections',
    icon: Compass,
    description: 'Logo strip showing authorized and supported device brands.',
    defaultContent: {
      title: 'Brands We Sell',
      eyebrow: 'DEVICE ECOSYSTEM',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="space-y-3">
        {content.eyebrow && (
          <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block text-center">
            {content.eyebrow}
          </span>
        )}
        <h2 className="text-xl font-extrabold text-slate-900 text-center">{content.title}</h2>
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 py-4 bg-slate-50 rounded-xl p-4">
          {['Apple', 'Samsung', 'OnePlus', 'Xiaomi', 'Vivo', 'Dell', 'HP'].map((brand, idx) => (
            <div key={idx} className="flex items-center gap-2 font-bold text-slate-700 text-sm hover:text-[#2b3380] cursor-pointer">
              <span className="w-7 h-7 bg-white rounded-full shadow-sm flex items-center justify-center text-xs font-mono font-extrabold">
                {brand[0]}
              </span>
              <span>{brand}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  testimonials: {
    type: 'testimonials',
    name: 'Customer Testimonials',
    category: 'collections',
    icon: MessageSquareQuote,
    description: 'Customer reviews with ratings, photos, and designations.',
    defaultContent: {
      title: 'Trusted by ZOBOX Customers',
      eyebrow: 'CUSTOMER STORIES',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="space-y-4">
        {content.eyebrow && (
          <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block text-center">
            {content.eyebrow}
          </span>
        )}
        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 text-center">{content.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'Rahul Sharma', city: 'Delhi', text: 'Zobox made it possible for me to own an iPhone 13 at half the MRP. Quality was like new!' },
            { name: 'Priya Verma', city: 'Noida', text: 'Outstanding laptop condition and 6 months warranty gave me complete peace of mind.' },
            { name: 'Amit Patel', city: 'Gurgaon', text: 'Fast delivery, proper GST invoice, and transparent battery health status. Highly recommended.' },
          ].map((t, idx) => (
            <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm space-y-2">
              <div className="flex items-center gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-current" />
                ))}
              </div>
              <p className="text-xs text-slate-600 leading-relaxed italic">"{t.text}"</p>
              <div className="pt-2 border-t text-xs">
                <strong className="text-slate-900 block">{t.name}</strong>
                <span className="text-slate-400">{t.city}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  store_cards: {
    type: 'store_cards',
    name: 'Store Locations Grid',
    category: 'collections',
    icon: Store,
    description: 'Retail stores with addresses, phone, and Google Maps directions.',
    defaultContent: {
      title: 'Find a ZOBOX Store',
      eyebrow: 'VERIFIED LOCATIONS',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="space-y-4">
        {content.eyebrow && (
          <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block">
            {content.eyebrow}
          </span>
        )}
        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">{content.title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { name: 'Karol Bagh Flagship', city: 'New Delhi', addr: '4th Floor, 6/77, WEA, Karol Bagh, New Delhi – 110005' },
            { name: 'Noida Corporate Experience', city: 'Noida', addr: 'E218C, Sector 63, Noida, Uttar Pradesh – 201301' },
          ].map((s, idx) => (
            <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#2b3380] flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <strong className="text-sm text-slate-900 block">{s.name} ({s.city})</strong>
                <p className="text-xs text-slate-600 leading-relaxed">{s.addr}</p>
                <span className="text-xs font-bold text-[#2b3380] inline-block pt-1 hover:underline">
                  Get Directions →
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },

  app_download: {
    type: 'app_download',
    name: 'App Download Banner',
    category: 'collections',
    icon: Smartphone,
    description: 'ZOBIZ app download badges for Google Play and App Store.',
    defaultContent: {
      title: 'Download the ZOBIZ App',
      description: 'Order wholesale devices, track warranty claims, and manage your inventory on the go.',
      googlePlayUrl: 'https://play.google.com/store/search?q=zobiz',
      appStoreUrl: 'https://www.apple.com/app-store/',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex flex-wrap items-center justify-between gap-6 shadow-xl">
        <div className="space-y-2 max-w-md">
          <h3 className="text-2xl font-extrabold">{content.title}</h3>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">{content.description}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 bg-black/50 border border-white/20 rounded-xl flex items-center gap-2 cursor-pointer hover:bg-black/70">
            <Smartphone className="w-5 h-5 text-emerald-400" />
            <div className="text-left text-[10px]">
              <span className="block text-slate-400 uppercase">Get it on</span>
              <strong className="text-xs font-bold text-white">Google Play</strong>
            </div>
          </div>
          <div className="px-4 py-2 bg-black/50 border border-white/20 rounded-xl flex items-center gap-2 cursor-pointer hover:bg-black/70">
            <Smartphone className="w-5 h-5 text-sky-400" />
            <div className="text-left text-[10px]">
              <span className="block text-slate-400 uppercase">Download on</span>
              <strong className="text-xs font-bold text-white">App Store</strong>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  faq_accordion: {
    type: 'faq_accordion',
    name: 'FAQ Accordion',
    category: 'collections',
    icon: HelpCircle,
    description: 'Frequently asked questions with collapsible answers.',
    defaultContent: {
      title: 'Frequently Asked Questions',
      eyebrow: 'HELP & SUPPORT',
      items: [
        { q: 'How do I get started with Zobiz Application?', a: 'Download the application from Play Store or App Store, complete simple KYC, and start placing orders.' },
        { q: 'What is the warranty period for refurbished phones?', a: 'Every ZOBOX certified device comes with 6 to 12 months comprehensive warranty support.' },
        { q: 'Is Cash on Delivery available?', a: 'Yes, COD is conditionally open to your eligibility grounds.' },
      ],
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const items = Array.isArray(content.items) ? content.items : [];
      return (
        <div className="space-y-4">
          {content.eyebrow && (
            <span className="text-xs font-bold uppercase tracking-wider text-[#2b3380] block">
              {content.eyebrow}
            </span>
          )}
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">{content.title || 'Frequently Asked Questions'}</h2>
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="border rounded-xl p-3 bg-white shadow-sm">
                <div className="flex items-center justify-between font-bold text-sm text-slate-900 cursor-pointer">
                  <span>{item.q || item.question}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed whitespace-pre-line">{item.a || item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      );
    },
  },

  feature_grid: {
    type: 'feature_grid',
    name: 'Feature Grid (9 Cards)',
    category: 'cards',
    icon: Sparkles,
    description: 'Grid of feature cards with icons, titles, and descriptions (e.g. Partner Benefits).',
    defaultContent: {
      title: 'Why Choose Zobiz Retailer App?',
      items: [
        { icon: '/svgs/becomePartner/warranty.svg', title: '12-Month Warranty for Peace of Mind', description: 'The devices sold through Zobiz come with up to a 12-month warranty, ensuring your customers receive reliable, high-quality products.' },
        { icon: '/svgs/becomePartner/functionalDevice.svg', title: '100% Functional Devices', description: 'Every device listed on the Zobiz platform is fully tested and guaranteed to be 100% functional.' },
        { icon: '/svgs/becomePartner/latestModel.svg', title: 'Access to the Latest Models', description: 'Stay ahead of the competition with new and popular models that are in demand.' },
        { icon: '/svgs/becomePartner/free-delivery_6912177 1.svg', title: 'Free Delivery', description: 'Zobiz offers free delivery on all orders, allowing you to focus on growing your business while we handle logistics.' },
        { icon: '/svgs/becomePartner/multiPayment.svg', title: 'Multiple Payment Options', description: 'Flexible and secure payment options including UPI, Cards, NetBanking, COD & credit terms.' },
        { icon: '/svgs/becomePartner/wholeSale.svg', title: 'Hassle-Free Wholesale Ordering', description: 'Easily browse and order refurbished devices without worrying about condition or quality.' },
        { icon: '/svgs/becomePartner/b2b.svg', title: 'Competitive B2B Pricing', description: 'Take advantage of exclusive wholesale pricing, volume discounts, and bulk margins.' },
        { icon: '/svgs/becomePartner/realTimeInvent.svg', title: 'Real-Time Inventory & Orders', description: 'Track and manage your inventory with real-time updates directly through the app.' },
        { icon: '/svgs/becomePartner/ecoFriendly.svg', title: 'Eco-Friendly Business Model', description: 'Offer eco-friendly devices to your customers, contributing to reducing e-waste.' },
      ],
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const items = Array.isArray(content.features) ? content.features : (Array.isArray(content.items) ? content.items : []);
      return (
        <div className="space-y-4 my-4">
          {content.title && <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{content.title}</h2>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((feat, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs hover:shadow-md transition">
                {(feat.icon || feat.image) && <img src={feat.icon || feat.image} alt="" className="w-8 h-8 mb-2 object-contain" onError={(e) => (e.target.style.display = 'none')} />}
                <h4 className="font-semibold text-sm text-slate-900 mb-1">{feat.title || `Feature ${idx + 1}`}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{feat.description || feat.desc || ''}</p>
              </div>
            ))}
          </div>
        </div>
      );
    },
  },

  step_process: {
    type: 'step_process',
    name: 'How It Works (Steps)',
    category: 'cards',
    icon: ListOrdered,
    description: 'Numbered steps showing onboarding or workflow steps with illustrations.',
    defaultContent: {
      title: 'How to Partner with Zobiz',
      steps: [
        { step: 1, image: '/svgs/becomePartner/step1.png', title: 'Download app', description: 'Download the Zobiz app from Google Play/App Store' },
        { step: 2, image: '/svgs/becomePartner/step2.png', title: 'Register yourself', description: 'Use business proof (GST / Pan / Aadhar) to register' },
        { step: 3, image: '/svgs/becomePartner/step3.png', title: 'Grow your business', description: 'Login and start placing wholesale orders' },
      ],
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const steps = Array.isArray(content.steps) ? content.steps : [];
      return (
        <div className="space-y-6 my-6 text-center">
          {content.title && <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{content.title}</h2>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {steps.map((st, idx) => {
              const imgSrc = st.image || st.img || st.src;
              const desc = st.description || st.desc || st.text;
              return (
                <div key={idx} className="flex flex-col items-center gap-3">
                  <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200 w-44 h-44 flex items-center justify-center">
                    {imgSrc && <img src={imgSrc} alt="" className="max-h-full max-w-full object-contain rounded-lg" onError={(e) => (e.target.style.display = 'none')} />}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900">{st.title || `Step ${idx + 1}`}</h4>
                    {desc && <p className="text-xs text-slate-600 mt-0.5">{desc}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
  },

  media_banner: {
    type: 'media_banner',
    name: 'Graphic / Infographic Banner',
    category: 'banners',
    icon: ImageIcon,
    description: 'Graphic banner or full infographic (e.g. Why Zobix, Success banner, Testimonial banner).',
    defaultContent: {
      image: '/banners/whyZobix.png',
      alt: 'ZOBOX Banner',
      href: '',
      caption: '',
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const imgSrc = content.image || content.url;
      return (
        <div className="w-full my-4 rounded-xl overflow-hidden shadow-xs">
          {imgSrc ? (
            <img src={imgSrc} alt={content.alt || ''} className="w-full h-auto object-cover rounded-xl" onError={(e) => (e.target.style.display = 'none')} />
          ) : (
            <div className="w-full h-32 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 text-xs">
              Select or enter an image URL
            </div>
          )}
        </div>
      );
    },
  },

  video_player: {
    type: 'video_player',
    name: 'Video Player',
    category: 'banners',
    icon: Video,
    description: 'Embedded video player with title and subtitle (e.g. Refurbishment Process).',
    defaultContent: {
      title: 'Our Refurbishment Process',
      description: 'Experience premium technology at a fraction of the cost with our expertly refurbished devices.',
      videoUrl: '/assets/RefurbiProcess.mp4',
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const vUrl = content.url || content.videoUrl || '/assets/RefurbiProcess.mp4';
      const isEmbed = /youtube\.com|youtu\.be|vimeo\.com/i.test(vUrl);
      return (
        <div className="py-6 px-4 text-center space-y-3">
          {content.title && <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{content.title}</h2>}
          {content.description && <p className="text-xs text-slate-600 max-w-xl mx-auto">{content.description}</p>}
          <div className="w-full max-w-4xl mx-auto rounded-xl overflow-hidden shadow-md bg-black aspect-video flex items-center justify-center">
            {isEmbed ? (
              <iframe src={vUrl} title={content.title || 'Video'} className="w-full h-full border-0" allowFullScreen />
            ) : (
              <video controls className="w-full h-auto" poster={content.poster}>
                <source src={vUrl} type="video/mp4" />
              </video>
            )}
          </div>
        </div>
      );
    },
  },

  contact_channels: {
    type: 'contact_channels',
    name: 'Direct Contact Actions',
    category: 'cards',
    icon: PhoneCall,
    description: 'WhatsApp, Call Us, and Email action buttons with contact illustration.',
    defaultContent: {
      illustration: '/assets/contactUS.png',
      whatsapp: 'https://wa.me/919873788964',
      whatsappLabel: 'Chat On Whatsapp',
      phone: '9873788964',
      phoneLabel: 'Call Us Directly',
      email: 'support@zobox.in',
      emailLabel: 'Drop Us An Email',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <div className="flex flex-col md:flex-row justify-center items-center gap-8 p-6 bg-slate-50 rounded-2xl border border-slate-200 my-4">
        {content.illustration && (
          <img src={content.illustration} alt="Contact" className="w-48 h-48 object-contain" onError={(e) => (e.target.style.display = 'none')} />
        )}
        <div className="flex flex-col gap-3 w-full md:w-80">
          <div className="flex items-center justify-between bg-white border border-slate-200 px-4 py-3 rounded-full shadow-xs">
            <span className="text-[#2B3380] font-semibold text-xs">{content.whatsappLabel || 'Chat On Whatsapp'}</span>
            <span className="p-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs">💬</span>
          </div>
          <div className="flex items-center justify-between bg-white border border-slate-200 px-4 py-3 rounded-full shadow-xs">
            <span className="text-[#2B3380] font-semibold text-xs">{content.phoneLabel || 'Call Us Directly'}</span>
            <span className="p-1.5 rounded-full bg-blue-50 text-[#2B3380] text-xs">📞</span>
          </div>
          <div className="flex items-center justify-between bg-white border border-slate-200 px-4 py-3 rounded-full shadow-xs">
            <span className="text-[#2B3380] font-semibold text-xs">{content.emailLabel || 'Drop Us An Email'}</span>
            <span className="p-1.5 rounded-full bg-indigo-50 text-[#2B3380] text-xs">✉️</span>
          </div>
        </div>
      </div>
    ),
  },

  reach_us_boxes: {
    type: 'reach_us_boxes',
    name: 'Reach Us Info Boxes',
    category: 'cards',
    icon: MapPin,
    description: '3 dark-blue information boxes (Connect with us, Corporate office, Head office).',
    defaultContent: {
      boxes: [
        { title: 'Connect with us:', text: 'For support: call 9873788964 or email support@zobox.in' },
        { title: 'Corporate Office:', text: 'Zobox Protech Limited. | E218C, Sector - 63, Noida, UP - 201301' },
        { title: 'Head Office:', text: 'Zobox Protech Limited. | 4th Floor, 6/77, WEA, Karol Bagh, New Delhi - 110005' },
      ],
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const boxes = Array.isArray(content.boxes) ? content.boxes : [];
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          {boxes.map((b, idx) => (
            <div key={idx} className="bg-[#2B3380] text-white p-5 rounded-xl shadow-md flex items-start gap-3">
              <span className="text-xl">📍</span>
              <div className="space-y-1 text-xs">
                <strong className="text-sm font-bold block">{b.title}</strong>
                <p className="text-slate-200 leading-relaxed">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      );
    },
  },

  grade_tabs: {
    type: 'grade_tabs',
    name: 'Grading Condition Tabs',
    category: 'cards',
    icon: Award,
    description: 'Condition grading criteria tabs (Excellent, Superb, Good) with points and sample photos.',
    defaultContent: {
      title: 'Our Grades Explanations',
      tabs: [
        { name: 'Excellent', points: ['Perfectly Working', '12 Month Warranty', 'No Functional Defects'], displayText: 'Perfect cosmetics, original display', physicalText: 'No visible scratches' },
        { name: 'Superb', points: ['Perfectly Working', '12 Month Warranty', 'No Functional Defects'], displayText: 'Minor hairline blemishes possible', physicalText: 'Fully intact casing' },
        { name: 'Good', points: ['Perfectly Working', '6 Month Warranty', 'No Functional Defects'], displayText: 'Noticeable signs of use at half price', physicalText: 'Mint functionality' },
      ],
    },
    defaultStyle: {},
    render: ({ content = {} }) => {
      const tabs = Array.isArray(content.tabs) ? content.tabs : [];
      return (
        <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-sm my-4 space-y-4">
          <h3 className="font-bold text-base text-slate-900">{content.title || 'Our Grades Explanations'}</h3>
          <div className="flex gap-2 border-b pb-2">
            {tabs.map((t, idx) => (
              <span key={idx} className={`px-3 py-1 rounded-lg text-xs font-bold ${idx === 0 ? 'bg-[#2B3380] text-white' : 'bg-slate-100 text-slate-700'}`}>
                {t.name}
              </span>
            ))}
          </div>
          {tabs[0] && (
            <div className="space-y-3 text-xs">
              <div className="space-y-1">
                {(tabs[0].points || []).map((pt, pIdx) => (
                  <div key={pIdx} className="flex items-center gap-1.5 text-slate-700">
                    <span className="text-emerald-600 font-bold">✓</span>
                    <span>{pt}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600"><strong>Display:</strong> {tabs[0].displayText}</p>
              <p className="text-slate-600"><strong>Physical:</strong> {tabs[0].physicalText}</p>
            </div>
          )}
        </div>
      );
    },
  },

  // ----------------------------------------------------
  // GLOBAL WEBSITE PARTS
  // ----------------------------------------------------
  header_embed: {
    type: 'header_embed',
    name: 'Website Header Preview',
    category: 'global',
    icon: Globe,
    description: 'Site-wide header with logo, navigation, search, and customer links.',
    defaultContent: {
      siteName: 'ZOBOX',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <header className="w-full bg-white border-b py-3 px-4 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-3">
          <span className="font-extrabold text-xl tracking-tight text-[#2b3380]">
            {content.siteName || 'ZOBOX'}
          </span>
          <nav className="hidden md:flex items-center gap-4 text-xs font-semibold text-slate-700 ml-6">
            <span className="hover:text-[#2b3380] cursor-pointer">Buy</span>
            <span className="hover:text-[#2b3380] cursor-pointer">Sell</span>
            <span className="hover:text-[#2b3380] cursor-pointer">ZOBIZ</span>
            <span className="hover:text-[#2b3380] cursor-pointer">Stores</span>
            <span className="hover:text-[#2b3380] cursor-pointer">About</span>
          </nav>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="hidden sm:block border rounded-lg px-2.5 py-1 text-slate-400 bg-slate-50 text-[11px]">
            🔍 Search devices...
          </div>
          <span className="text-[#2b3380] font-semibold cursor-pointer">Login / Account</span>
        </div>
      </header>
    ),
  },

  footer_embed: {
    type: 'footer_embed',
    name: 'Website Footer Preview',
    category: 'global',
    icon: Layers,
    description: 'Site-wide footer with ecosystem, company, help links, and copyright.',
    defaultContent: {
      copyrightText: '© 2026 Zobox Protech Limited. All rights reserved.',
    },
    defaultStyle: {},
    render: ({ content = {} }) => (
      <footer className="w-full bg-slate-900 text-slate-300 py-6 px-6 rounded-xl space-y-4 text-xs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <strong className="text-white block mb-2">Ecosystem</strong>
            <ul className="space-y-1 text-slate-400">
              <li>Buy Devices</li>
              <li>Sell Devices</li>
              <li>ZOBIZ Partner</li>
            </ul>
          </div>
          <div>
            <strong className="text-white block mb-2">Company</strong>
            <ul className="space-y-1 text-slate-400">
              <li>About ZOBOX</li>
              <li>Stores</li>
              <li>Press Releases</li>
            </ul>
          </div>
          <div>
            <strong className="text-white block mb-2">Support</strong>
            <ul className="space-y-1 text-slate-400">
              <li>Warranty Claim</li>
              <li>Help Desk</li>
              <li>Contact Us</li>
            </ul>
          </div>
          <div>
            <strong className="text-white block mb-2">Offices</strong>
            <p className="text-slate-400 text-[11px]">Sector 63, Noida &amp; Karol Bagh, Delhi</p>
          </div>
        </div>
        <div className="pt-4 border-t border-slate-800 text-center text-slate-500 text-[11px]">
          {content.copyrightText || '© 2026 Zobox Protech Limited. All rights reserved.'}
        </div>
      </footer>
    ),
  },
};
